/*
 This file is part of GNU Taler
 (C) 2017-2019 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

/**
 * Helpers for relative and absolute time.
 */

/**
 * Imports.
 */
import { Codec, Context, renderContext } from "./codec.js";

/* eslint-disable @typescript-eslint/no-unused-vars -- Declared symbols brand exported types. */
declare const flavor_AbsoluteTime: unique symbol;
declare const flavor_TalerProtocolTimestamp: unique symbol;
declare const flavor_TalerPreciseTimestamp: unique symbol;
/* eslint-enable @typescript-eslint/no-unused-vars */

const opaque_AbsoluteTime: unique symbol = Symbol("opaque_AbsoluteTime");

// FIXME: Make this opaque!
export interface AbsoluteTime {
  /**
   * Timestamp in milliseconds.
   */
  readonly t_ms: number | "never";

  readonly _flavor?: typeof flavor_AbsoluteTime;

  // Make the type opaque, we only want our constructors
  // to able to create an AbsoluteTime value.
  [opaque_AbsoluteTime]: true;
}

export interface TalerProtocolTimestamp {
  /**
   * Seconds (as integer) since epoch.
   */
  readonly t_s: number | "never";

  readonly _flavor?: typeof flavor_TalerProtocolTimestamp;
}

/**
 * Precise timestamp, typically used in the wallet-core
 * API but not in other Taler APIs so far.
 */
export interface TalerPreciseTimestamp {
  /**
   * Seconds (as integer) since epoch.
   */
  readonly t_s: number | "never";

  /**
   * Optional microsecond offset (non-negative integer).
   */
  readonly off_us?: number;

  readonly _flavor?: typeof flavor_TalerPreciseTimestamp;
}

export namespace TalerPreciseTimestamp {
  export function now(): TalerPreciseTimestamp {
    const absNow = AbsoluteTime.now();
    return AbsoluteTime.toPreciseTimestamp(absNow);
  }

  export function round(t: TalerPreciseTimestamp): TalerProtocolTimestamp {
    return {
      t_s: t.t_s,
    };
  }

  export function fromSeconds(s: number): TalerPreciseTimestamp {
    return {
      t_s: Math.floor(s),
      off_us: Math.floor((s - Math.floor(s)) * 1e6),
    };
  }

  export function fromMilliseconds(ms: number): TalerPreciseTimestamp {
    return {
      t_s: Math.floor(ms / 1000),
      off_us: Math.floor((ms - Math.floor(ms / 1000) * 1000) * 1000),
    };
  }
}

export namespace TalerProtocolDuration {
  export function fromSpec(d: DurationUnitSpec) {
    return Duration.toTalerProtocolDuration(Duration.fromSpec(d));
  }

  export function forever(): TalerProtocolDuration {
    return {
      d_us: "forever",
    };
  }
}

export namespace TalerProtocolTimestamp {
  export function isTimestamp(x: unknown): x is TalerProtocolTimestamp {
    return (
      typeof x === "object" &&
      x !== null &&
      "t_s" in x &&
      (typeof x.t_s === "number" || x.t_s === "never")
    );
  }
  export function now(): TalerProtocolTimestamp {
    return AbsoluteTime.toProtocolTimestamp(AbsoluteTime.now());
  }

  export function zero(): TalerProtocolTimestamp {
    return {
      t_s: 0,
    };
  }

  export function never(): TalerProtocolTimestamp {
    return {
      t_s: "never",
    };
  }

  export function isNever(t: TalerProtocolTimestamp): boolean {
    return t.t_s === "never";
  }

  export function fromSeconds(s: number): TalerProtocolTimestamp {
    return {
      t_s: Math.floor(s),
    };
  }

  export function fromMilliseconds(ms: number): TalerProtocolTimestamp {
    return {
      t_s: Math.floor(ms / 1000),
    };
  }

  export function min(
    t1: TalerProtocolTimestamp,
    t2: TalerProtocolTimestamp,
  ): TalerProtocolTimestamp {
    if (t1.t_s === "never") {
      return { t_s: t2.t_s };
    }
    if (t2.t_s === "never") {
      return { t_s: t1.t_s };
    }
    return { t_s: Math.min(t1.t_s, t2.t_s) };
  }

  export function max(
    t1: TalerProtocolTimestamp,
    t2: TalerProtocolTimestamp,
  ): TalerProtocolTimestamp {
    if (t1.t_s === "never" || t2.t_s === "never") {
      return { t_s: "never" };
    }
    return { t_s: Math.max(t1.t_s, t2.t_s) };
  }
}

export interface Duration {
  /**
   * Duration in milliseconds.
   */
  readonly d_ms: number | "forever";
}

export interface TalerProtocolDuration {
  readonly d_us: number | "forever";
}

export type DurationLike = TalerProtocolDuration | Duration;

export type TimestampLike =
  | AbsoluteTime
  | { t_ms: number | "never" }
  | TalerProtocolTimestamp
  | TalerPreciseTimestamp
  | Date
  | number
  | string
  | Record<string, unknown>;

export type DateFormatPattern = "ymd" | "dmy" | "mdy";

export interface FormatDateOptions {
  dateFormat?: DateFormatPattern;
  includeTime?: boolean;
  includeSeconds?: boolean;
}

/**
 * Timeshift in milliseconds.
 */
let timeshift = 0;

/**
 * Set timetravel offset in milliseconds.
 *
 * Use carefully and only for testing.
 */
export function setDangerousTimetravel(dt: number): void {
  timeshift = dt;
}

export interface DurationUnitSpec {
  seconds?: number;
  minutes?: number;
  hours?: number;
  days?: number;
  months?: number;
  years?: number;
}

export namespace Duration {
  export function toMilliseconds(d: Duration): number {
    if (d.d_ms === "forever") {
      return Number.MAX_VALUE;
    }
    return d.d_ms;
  }
  export function getRemaining(
    deadline: AbsoluteTime,
    now = AbsoluteTime.now(),
  ): Duration {
    if (deadline.t_ms === "never") {
      return { d_ms: "forever" };
    }
    if (now.t_ms === "never") {
      throw Error("invalid argument for 'now'");
    }
    if (deadline.t_ms < now.t_ms) {
      return { d_ms: 0 };
    }
    return { d_ms: deadline.t_ms - now.t_ms };
  }

  export function fromPrettyString(s: string): Duration {
    // Keep this grammar in sync with GNUNET_STRINGS_fancy_time_to_relative().
    // A bare number is microseconds, and numbers and their units may either be
    // adjacent ("15m") or separated by spaces ("15 m").
    if (s.trim().toLowerCase() === "forever") {
      return { d_ms: "forever" };
    }
    const unitUs: Record<string, number> = {
      us: 1,
      ms: 1_000,
      s: 1_000_000,
      second: 1_000_000,
      seconds: 1_000_000,
      '"': 1_000_000,
      m: 60 * 1_000_000,
      min: 60 * 1_000_000,
      minute: 60 * 1_000_000,
      minutes: 60 * 1_000_000,
      "'": 60 * 1_000_000,
      h: 60 * 60 * 1_000_000,
      hour: 60 * 60 * 1_000_000,
      hours: 60 * 60 * 1_000_000,
      d: 24 * 60 * 60 * 1_000_000,
      day: 24 * 60 * 60 * 1_000_000,
      days: 24 * 60 * 60 * 1_000_000,
      w: 7 * 24 * 60 * 60 * 1_000_000,
      week: 7 * 24 * 60 * 60 * 1_000_000,
      weeks: 7 * 24 * 60 * 60 * 1_000_000,
      y: 365 * 24 * 60 * 60 * 1_000_000,
      a: 365 * 24 * 60 * 60 * 1_000_000,
      year: 365 * 24 * 60 * 60 * 1_000_000,
      years: 365 * 24 * 60 * 60 * 1_000_000,
    };

    let totalUs = 0;
    let pending = 0;
    for (const token of s.split(" ").filter(Boolean)) {
      const unit = unitUs[token.toLowerCase()];
      if (unit !== undefined) {
        pending *= unit;
      } else {
        const match = /^(\d+)(.*)$/.exec(token);
        if (!match) {
          throw Error("invalid duration, expected a number or unit");
        }
        totalUs += pending;
        pending = Number(match[1]);
        if (!Number.isSafeInteger(pending)) {
          throw Error("invalid duration, number is too large");
        }
        if (match[2]) {
          const attachedUnit = unitUs[match[2].toLowerCase()];
          if (attachedUnit === undefined) {
            throw Error("invalid duration, unsupported unit");
          }
          pending *= attachedUnit;
        }
      }
      if (!Number.isSafeInteger(totalUs) || !Number.isSafeInteger(pending)) {
        throw Error("invalid duration, value is too large");
      }
    }
    totalUs += pending;
    if (!Number.isSafeInteger(totalUs)) {
      throw Error("invalid duration, value is too large");
    }
    return { d_ms: totalUs / 1_000 };
  }

  /**
   * Compare two durations.  Returns 0 when equal, -1 when a < b
   * and +1 when a > b.
   */
  export function cmp(d1: Duration, d2: Duration): 1 | 0 | -1 {
    if (d1.d_ms === "forever") {
      if (d2.d_ms === "forever") {
        return 0;
      }
      return 1;
    }
    if (d2.d_ms === "forever") {
      return -1;
    }
    if (d1.d_ms == d2.d_ms) {
      return 0;
    }
    if (d1.d_ms > d2.d_ms) {
      return 1;
    }
    return -1;
  }

  export function add(d1: Duration, d2: Duration): Duration {
    if (d1.d_ms === "forever") {
      return Duration.getForever();
    }
    if (d2.d_ms === "forever") {
      return Duration.getForever();
    }
    return Duration.fromMilliseconds(d1.d_ms + d2.d_ms);
  }

  export function max(d1: Duration, d2: Duration): Duration {
    return durationMax(d1, d2);
  }

  export function min(d1: Duration, d2: Duration): Duration {
    return durationMin(d1, d2);
  }

  export function multiply(d1: Duration, n: number): Duration {
    return durationMul(d1, n);
  }

  export function toIntegerYears(d: Duration): number {
    if (typeof d.d_ms !== "number") {
      throw Error("infinite duration");
    }
    return Math.ceil(d.d_ms / 1000 / 60 / 60 / 24 / 365);
  }

  /**
   * Construct a duration from a specification of the individual units.
   *
   * Returns a zero duration if none of the units were specified.
   */
  export function fromSpec(spec: DurationUnitSpec): Duration {
    let d_ms = 0;
    d_ms += (spec.seconds ?? 0) * SECONDS;
    d_ms += (spec.minutes ?? 0) * MINUTES;
    d_ms += (spec.hours ?? 0) * HOURS;
    d_ms += (spec.days ?? 0) * DAYS;
    d_ms += (spec.months ?? 0) * MONTHS;
    d_ms += (spec.years ?? 0) * YEARS;
    return { d_ms };
  }

  export function fromSpecOrUndefined(
    spec: DurationUnitSpec,
  ): Duration | undefined {
    if (
      spec.seconds == undefined &&
      spec.minutes == undefined &&
      spec.hours == undefined &&
      spec.days == undefined &&
      spec.months == undefined &&
      spec.years == undefined
    ) {
      return undefined;
    }

    return Duration.fromSpec(spec);
  }

  export function toSpec({ d_ms }: Duration):
    | {
        seconds: number;
        minutes: number;
        hours: number;
        days: number;
        months: number;
        years: number;
      }
    | undefined {
    if (d_ms === "forever") return undefined;
    const ms = d_ms > 0 ? d_ms : 0;
    const Y_rest = ms % YEARS;
    const M_rest = Y_rest % MONTHS;
    const D_rest = M_rest % DAYS;
    const h_rest = D_rest % HOURS;
    const m_rest = h_rest % MINUTES;
    const millis = m_rest % SECONDS;

    return {
      years: (ms - Y_rest) / YEARS,
      months: (Y_rest - M_rest) / MONTHS,
      days: (M_rest - D_rest) / DAYS,
      hours: (D_rest - h_rest) / HOURS,
      minutes: (h_rest - m_rest) / MINUTES,
      seconds: (m_rest - millis) / SECONDS,
    };
  }

  export function getForever(): Duration {
    return { d_ms: "forever" };
  }

  export function isForever(d: Duration): boolean {
    return d.d_ms === "forever";
  }

  export function getZero(): Duration {
    return { d_ms: 0 };
  }

  export function fromTalerProtocolDuration(
    d: TalerProtocolDuration,
  ): Duration {
    if (d.d_us === "forever") {
      return {
        d_ms: "forever",
      };
    }
    return {
      d_ms: d.d_us / 1000,
    };
  }

  export function toTalerProtocolDuration(d: Duration): TalerProtocolDuration {
    if (d.d_ms === "forever") {
      return {
        d_us: "forever",
      };
    }
    return {
      // A Duration stores milliseconds as a JavaScript number.  A value such
      // as 1.001 ms is an exact 1001-us protocol duration, but multiplication
      // can produce 1000.9999999999999 in binary floating point.
      d_us: Math.round(d.d_ms * 1000),
    };
  }

  export function fromMilliseconds(ms: number): Duration {
    return {
      d_ms: ms,
    };
  }

  export function clamp(args: {
    lower: Duration;
    upper: Duration;
    value: Duration;
  }): Duration {
    return durationMax(durationMin(args.value, args.upper), args.lower);
  }

  /**
   * Render a Duration or TalerProtocolDuration as a short duration string
   * ("30m", "2h", "7d", "Forever"). Returns undefined when unset or <= 0.
   */
  export function formatShort(
    duration: DurationLike | undefined | null,
  ): string | undefined {
    if (!duration) return undefined;
    let us: number | "forever";
    if ("d_us" in duration) {
      us = duration.d_us;
    } else if ("d_ms" in duration) {
      us =
        duration.d_ms === "forever"
          ? "forever"
          : Math.round(duration.d_ms * 1000);
    } else {
      return undefined;
    }
    if (us === "forever") return "Forever";
    if (typeof us !== "number" || !isFinite(us) || us <= 0) return undefined;
    const MINUTE_US = 60 * 1_000_000;
    const HOUR_US = 60 * MINUTE_US;
    const DAY_US = 24 * HOUR_US;
    if (us % DAY_US === 0) return `${us / DAY_US}d`;
    if (us % HOUR_US === 0) return `${us / HOUR_US}h`;
    if (us % MINUTE_US === 0) return `${us / MINUTE_US}m`;
    return `${Math.round(us / MINUTE_US)}m`;
  }

  /**
   * Render a finite duration in the complete grammar accepted by
   * {@link fromPrettyString}, with the largest units first.
   *
   * Unlike {@link formatShort}, this retains the seconds part of mixed values,
   * making it suitable for showing a normalized form of a duration a user
   * entered in the free-form duration input.
   */
  export function formatPretty(
    duration: DurationLike | undefined | null,
  ): string | undefined {
    if (!duration) return undefined;
    let us: number | "forever";
    if ("d_us" in duration) {
      us = duration.d_us;
    } else if ("d_ms" in duration) {
      us =
        duration.d_ms === "forever"
          ? "forever"
          : Math.round(duration.d_ms * 1000);
    } else {
      return undefined;
    }
    if (us === "forever") return "forever";
    if (typeof us !== "number" || !isFinite(us) || us < 0) {
      return undefined;
    }
    if (!Number.isSafeInteger(us)) return undefined;

    const YEAR_US = 365 * 24 * 60 * 60 * 1_000_000;
    const WEEK_US = 7 * 24 * 60 * 60 * 1_000_000;
    const DAY_US = 24 * 60 * 60 * 1_000_000;
    const HOUR_US = 60 * 60 * 1_000_000;
    const MINUTE_US = 60 * 1_000_000;
    const SECOND_US = 1_000_000;
    const years = Math.floor(us / YEAR_US);
    us -= years * YEAR_US;
    const weeks = Math.floor(us / WEEK_US);
    us -= weeks * WEEK_US;
    const days = Math.floor(us / DAY_US);
    us -= days * DAY_US;
    const hours = Math.floor(us / HOUR_US);
    us -= hours * HOUR_US;
    const minutes = Math.floor(us / MINUTE_US);
    us -= minutes * MINUTE_US;
    const seconds = Math.floor(us / SECOND_US);
    us -= seconds * SECOND_US;
    const milliseconds = Math.floor(us / 1_000);
    us -= milliseconds * 1_000;

    const pieces: string[] = [];
    if (years) pieces.push(`${years}y`);
    if (weeks) pieces.push(`${weeks}w`);
    if (days) pieces.push(`${days}d`);
    if (hours) pieces.push(`${hours}h`);
    if (minutes) pieces.push(`${minutes}m`);
    if (seconds) pieces.push(`${seconds}s`);
    if (milliseconds) pieces.push(`${milliseconds}ms`);
    if (us || pieces.length === 0) pieces.push(`${us}us`);
    return pieces.join(" ");
  }
}

export namespace AbsoluteTime {
  export function getStampMsNow(): number {
    return new Date().getTime();
  }

  export function getStampMsNever(): number {
    return Number.MAX_SAFE_INTEGER;
  }

  export function now(): AbsoluteTime {
    return {
      t_ms: new Date().getTime() + timeshift,
      [opaque_AbsoluteTime]: true,
    };
  }

  export function zero(): AbsoluteTime {
    return {
      t_ms: 0,
      [opaque_AbsoluteTime]: true,
    };
  }

  export function never(): AbsoluteTime {
    return {
      t_ms: "never",
      [opaque_AbsoluteTime]: true,
    };
  }

  export function fromMilliseconds(ms: number): AbsoluteTime {
    return {
      t_ms: ms,
      [opaque_AbsoluteTime]: true,
    };
  }

  export function cmp(t1: AbsoluteTime, t2: AbsoluteTime): number {
    if (t1.t_ms === "never") {
      if (t2.t_ms === "never") {
        return 0;
      }
      return 1;
    }
    if (t2.t_ms === "never") {
      return -1;
    }
    if (t1.t_ms == t2.t_ms) {
      return 0;
    }
    if (t1.t_ms > t2.t_ms) {
      return 1;
    }
    return -1;
  }

  export function min(t1: AbsoluteTime, t2: AbsoluteTime): AbsoluteTime {
    if (t1.t_ms === "never") {
      return { t_ms: t2.t_ms, [opaque_AbsoluteTime]: true };
    }
    if (t2.t_ms === "never") {
      return { t_ms: t1.t_ms, [opaque_AbsoluteTime]: true };
    }
    return { t_ms: Math.min(t1.t_ms, t2.t_ms), [opaque_AbsoluteTime]: true };
  }

  export function max(t1: AbsoluteTime, t2: AbsoluteTime): AbsoluteTime {
    if (t1.t_ms === "never") {
      return { t_ms: "never", [opaque_AbsoluteTime]: true };
    }
    if (t2.t_ms === "never") {
      return { t_ms: "never", [opaque_AbsoluteTime]: true };
    }
    return { t_ms: Math.max(t1.t_ms, t2.t_ms), [opaque_AbsoluteTime]: true };
  }

  export function difference(t1: AbsoluteTime, t2: AbsoluteTime): Duration {
    if (t1.t_ms === "never") {
      return { d_ms: "forever" };
    }
    if (t2.t_ms === "never") {
      return { d_ms: "forever" };
    }
    return { d_ms: Math.abs(t1.t_ms - t2.t_ms) };
  }

  export function isExpired(t: AbsoluteTime) {
    return cmp(t, now()) <= 0;
  }

  export function isNever(t: AbsoluteTime): boolean {
    return t.t_ms === "never";
  }

  export function fromProtocolTimestamp(
    t: TalerProtocolTimestamp,
  ): AbsoluteTime {
    if (t.t_s === "never") {
      return { t_ms: "never", [opaque_AbsoluteTime]: true };
    }
    return {
      t_ms: t.t_s * 1000,
      [opaque_AbsoluteTime]: true,
    };
  }

  export function fromStampMs(stampMs: number): AbsoluteTime {
    return {
      t_ms: stampMs,
      [opaque_AbsoluteTime]: true,
    };
  }

  export function fromPreciseTimestamp(t: TalerPreciseTimestamp): AbsoluteTime {
    if (t.t_s === "never") {
      return { t_ms: "never", [opaque_AbsoluteTime]: true };
    }
    const offsetUs = t.off_us ?? 0;
    return {
      t_ms: t.t_s * 1000 + Math.floor(offsetUs / 1000),
      [opaque_AbsoluteTime]: true,
    };
  }

  export function toStampMs(at: AbsoluteTime): number {
    if (at.t_ms === "never") {
      return Number.MAX_SAFE_INTEGER;
    }
    return at.t_ms;
  }

  export function toPreciseTimestamp(at: AbsoluteTime): TalerPreciseTimestamp {
    if (at.t_ms == "never") {
      return {
        t_s: "never",
      };
    }
    const t_s = Math.floor(at.t_ms / 1000);
    const off_us = Math.floor(1000 * (at.t_ms - t_s * 1000));
    return {
      t_s,
      off_us,
    };
  }

  export function toProtocolTimestamp(
    at: AbsoluteTime,
  ): TalerProtocolTimestamp {
    if (at.t_ms === "never") {
      return { t_s: "never" };
    }
    return {
      t_s: Math.floor(at.t_ms / 1000),
    };
  }

  export function isBetween(
    t: AbsoluteTime,
    start: AbsoluteTime,
    end: AbsoluteTime,
  ): boolean {
    if (cmp(t, start) < 0) {
      return false;
    }
    if (cmp(t, end) > 0) {
      return false;
    }
    return true;
  }

  export function toIsoString(t: AbsoluteTime): string {
    if (t.t_ms === "never") {
      return "<never>";
    } else {
      return new Date(t.t_ms).toISOString();
    }
  }

  export function addDuration(t1: AbsoluteTime, d: Duration): AbsoluteTime {
    if (t1.t_ms === "never" || d.d_ms === "forever") {
      return { t_ms: "never", [opaque_AbsoluteTime]: true };
    }
    return { t_ms: t1.t_ms + d.d_ms, [opaque_AbsoluteTime]: true };
  }

  /**
   * Get the remaining duration until `t1`.
   *
   * If `t1` already happened, the remaining duration
   * is zero.
   */
  export function remaining(t1: AbsoluteTime): Duration {
    if (t1.t_ms === "never") {
      return Duration.getForever();
    }
    const stampNow = now();
    if (stampNow.t_ms === "never") {
      throw Error("invariant violated");
    }
    return Duration.fromMilliseconds(Math.max(0, t1.t_ms - stampNow.t_ms));
  }

  export function subtractDuraction(
    t1: AbsoluteTime,
    d: Duration,
  ): AbsoluteTime {
    if (t1.t_ms === "never") {
      return { t_ms: "never", [opaque_AbsoluteTime]: true };
    }
    if (d.d_ms === "forever") {
      return { t_ms: 0, [opaque_AbsoluteTime]: true };
    }
    return { t_ms: Math.max(0, t1.t_ms - d.d_ms), [opaque_AbsoluteTime]: true };
  }

  export function stringify(t: AbsoluteTime): string {
    if (t.t_ms === "never") {
      return "never";
    }
    return new Date(t.t_ms).toISOString();
  }

  /**
   * Parse a TimestampLike value (AbsoluteTime, TalerProtocolTimestamp,
   * TalerPreciseTimestamp, Date, number, or ISO string) into a millisecond timestamp.
   * Returns undefined if nullish, invalid, or "never".
   */
  export function parseTimestampMs(
    val: TimestampLike | undefined | null,
  ): number | undefined {
    if (!val) return undefined;
    if (typeof val === "number") return val > 1e11 ? val : val * 1000;
    if (val instanceof Date)
      return isNaN(val.getTime()) ? undefined : val.getTime();
    if (typeof val === "string") {
      if (val === "never") return undefined;
      const parsed = Date.parse(val);
      return isNaN(parsed) ? undefined : parsed;
    }
    if (typeof val === "object") {
      const v = val as Record<string, unknown>;
      if (v.t_s === "never" || v.t_ms === "never") return undefined;
      if (typeof v.t_ms === "number") return v.t_ms;
      if (typeof v.t_s === "number") {
        const offUs = typeof v.off_us === "number" ? v.off_us : 0;
        return v.t_s * 1000 + Math.floor(offUs / 1000);
      }
      if (typeof v.d_ms === "number") return v.d_ms;
      if (typeof v.d_us === "number") return Math.floor(v.d_us / 1000);
    }
    return undefined;
  }

  /**
   * Format a TimestampLike value according to a date format pattern ("ymd", "dmy", "mdy")
   * and optional time options.
   */
  export function formatDate(
    input: TimestampLike | null | undefined,
    options?: FormatDateOptions,
  ): string {
    if (input === null || input === undefined || input === "") return "—";

    const ms = parseTimestampMs(input);
    if (ms === undefined) {
      return String(input);
    }

    const d = new Date(ms);
    const yyyy = String(d.getFullYear());
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");

    let datePart: string;
    const format = options?.dateFormat || "ymd";
    switch (format) {
      case "dmy":
        datePart = `${dd}/${mm}/${yyyy}`;
        break;
      case "mdy":
        datePart = `${mm}/${dd}/${yyyy}`;
        break;
      case "ymd":
      default:
        datePart = `${yyyy}/${mm}/${dd}`;
        break;
    }

    if (!options?.includeTime) {
      return datePart;
    }

    const hh = String(d.getHours()).padStart(2, "0");
    const min = String(d.getMinutes()).padStart(2, "0");
    if (options.includeSeconds) {
      const ss = String(d.getSeconds()).padStart(2, "0");
      return `${datePart} ${hh}:${min}:${ss}`;
    }

    return `${datePart} ${hh}:${min}`;
  }

  /**
   * Parse a TimestampLike value into a valid Date object, or null if empty or unparsable.
   */
  export function toDate(input: TimestampLike | null | undefined): Date | null {
    const ms = parseTimestampMs(input);
    if (ms !== undefined) return new Date(ms);
    if (typeof input === "string" && input.includes(" ")) {
      const d = new Date(input.replace(" ", "T"));
      return isNaN(d.getTime()) ? null : d;
    }
    return null;
  }

  /**
   * Format a past date or timestamp as a concise relative time string (e.g. "just now", "< 30s ago", "2m ago", "1h ago").
   */
  export function formatRelativeTime(
    input: TimestampLike | null | undefined,
    nowMs = Date.now(),
  ): string {
    const d = toDate(input);
    if (!d) return "";

    const diffSec = Math.max(0, Math.floor((nowMs - d.getTime()) / 1000));
    if (diffSec < 10) return "just now";
    if (diffSec < 30) return "< 30s ago";
    if (diffSec < 60) return "< 1m ago";
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHours = Math.floor(diffMin / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  }

  /**
   * Convenience helper to format date with hours and minutes according to options.
   */
  export function formatDateTime(
    input: TimestampLike | null | undefined,
    options?: FormatDateOptions,
  ): string {
    return formatDate(input, { ...options, includeTime: true });
  }

  /**
   * Format a TimestampLike value according to options and a fallback for empty/never values.
   */
  export function formatTimestamp(
    val: TimestampLike | undefined | null,
    fallback = "N/A",
    options?: FormatDateOptions,
  ): string {
    if (!val) return fallback;
    if (
      val === "never" ||
      (typeof val === "object" && "t_s" in val && val.t_s === "never")
    ) {
      return "Never";
    }
    const ms = parseTimestampMs(val);
    if (ms !== undefined && !isNaN(ms)) {
      return formatDateTime(ms, options);
    }
    if (typeof val === "string") return val;
    return fallback;
  }
}

const SECONDS = 1000;
const MINUTES = SECONDS * 60;
const HOURS = MINUTES * 60;
const DAYS = HOURS * 24;
const MONTHS = DAYS * 30;
const YEARS = DAYS * 365;

export function durationMin(d1: Duration, d2: Duration): Duration {
  if (d1.d_ms === "forever") {
    return { d_ms: d2.d_ms };
  }
  if (d2.d_ms === "forever") {
    return { d_ms: d1.d_ms };
  }
  return { d_ms: Math.min(d1.d_ms, d2.d_ms) };
}

export function durationMax(d1: Duration, d2: Duration): Duration {
  if (d1.d_ms === "forever") {
    return { d_ms: "forever" };
  }
  if (d2.d_ms === "forever") {
    return { d_ms: "forever" };
  }
  return { d_ms: Math.max(d1.d_ms, d2.d_ms) };
}

export function durationMul(d: Duration, n: number): Duration {
  if (d.d_ms === "forever") {
    return { d_ms: "forever" };
  }
  return { d_ms: Math.round(d.d_ms * n) };
}

/**
 * The protocol specification declares every timestamp and duration as an
 * Integer, capped at 2^53 - 1.  Values outside that -- NaN in particular,
 * which serializes back to null and compares as smaller than everything --
 * would otherwise silently propagate into expiry checks.
 */
function checkTimeValue(
  n: number,
  what: string,
  c: Context | undefined,
): number {
  if (!Number.isSafeInteger(n) || n < 0) {
    throw Error(`expected ${what} at ${renderContext(c)}`);
  }
  return n;
}

export const codecForAbsoluteTime: Codec<AbsoluteTime> = {
  decode(x: any, c?: Context): AbsoluteTime {
    if (x === undefined) {
      throw Error(
        `got undefined and expected absolute time at ${renderContext(c)}`,
      );
    }
    const t_ms = x.t_ms;
    if (typeof t_ms === "string") {
      if (t_ms === "never") {
        return { t_ms: "never", [opaque_AbsoluteTime]: true };
      }
    } else if (typeof t_ms === "number") {
      return {
        t_ms: checkTimeValue(t_ms, "timestamp", c),
        [opaque_AbsoluteTime]: true,
      };
    }
    throw Error(`expected timestamp at ${renderContext(c)}`);
  },
};

export const codecForTimestamp: Codec<TalerProtocolTimestamp> = {
  decode(x: any, c?: Context): TalerProtocolTimestamp {
    // Compatibility, should be removed soon.
    if (x === undefined) {
      throw Error(
        `got undefined and expected timestamp at ${renderContext(c)}`,
      );
    }
    const t_ms = x.t_ms;
    if (typeof t_ms === "string") {
      if (t_ms === "never") {
        return { t_s: "never" };
      }
    } else if (typeof t_ms === "number") {
      return { t_s: Math.floor(checkTimeValue(t_ms, "timestamp", c) / 1000) };
    }
    const t_s = x.t_s;
    if (typeof t_s === "string") {
      if (t_s === "never") {
        return { t_s: "never" };
      }
      throw Error(`expected timestamp at ${renderContext(c)}`);
    }
    if (typeof t_s === "number") {
      return { t_s: checkTimeValue(t_s, "protocol timestamp", c) };
    }
    throw Error(`expected protocol timestamp at ${renderContext(c)}`);
  },
};

export const codecForPreciseTimestamp: Codec<TalerPreciseTimestamp> = {
  decode(x: any, c?: Context): TalerPreciseTimestamp {
    const t_s = x.t_s;
    if (typeof t_s === "string") {
      if (t_s === "never") {
        return { t_s: "never" };
      }
      throw Error(`expected precise timestamp at ${renderContext(c)}`);
    }
    if (typeof t_s === "number") {
      checkTimeValue(t_s, "precise timestamp", c);
      const off_us = x.off_us;
      if (off_us === undefined) {
        return { t_s };
      }
      if (typeof off_us === "number") {
        return { t_s, off_us: checkTimeValue(off_us, "precise timestamp", c) };
      }
      throw Error(`expected precise timestamp at ${renderContext(c)}`);
    }

    throw Error(`expected precise timestamp at ${renderContext(c)}`);
  },
};

export const codecForDuration: Codec<TalerProtocolDuration> = {
  decode(x: any, c?: Context): TalerProtocolDuration {
    const d_us = x.d_us;
    if (typeof d_us === "string") {
      if (d_us === "forever") {
        return { d_us: "forever" };
      }
      throw Error(`expected duration at ${renderContext(c)}`);
    }
    if (typeof d_us === "number") {
      return { d_us: checkTimeValue(d_us, "duration", c) };
    }
    throw Error(`expected duration at ${renderContext(c)}`);
  },
};

export const codecForDurationMs: Codec<Duration> = {
  decode(x: any, c?: Context): Duration {
    const d_ms = x.d_ms;
    if (typeof d_ms === "string") {
      if (d_ms === "forever") {
        return { d_ms: "forever" };
      }
      throw Error(`expected duration at ${renderContext(c)}`);
    }
    if (typeof d_ms === "number") {
      return { d_ms: checkTimeValue(d_ms, "duration", c) };
    }
    throw Error(`expected duration at ${renderContext(c)}`);
  },
};
